import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Ticket, Search, MoreVertical, Trash2, Edit, ArrowRightCircle, Link2, Unlink,
  Check, Mail, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Pagination } from '../components/Pagination';
import { format } from '../utils/dateUtils';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

const MAX_DESCRIPTION = 2000;

export const TICKET_TYPES = [
  { value: 'question', label: 'Question', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'bug', label: 'Bug', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'remark', label: 'Remark', color: 'bg-gray-100 text-gray-800 border-gray-300' },
];

const EMPTY_FORM = {
  description: '',
  customer_name: '',
  app_name: '',
  type: '',
  email: '',
  phone: '',
  status: 'open'
};

export const getTicketTypeBadge = (type) => {
  const t = TICKET_TYPES.find((tt) => tt.value === type);
  if (!t) return null;
  return <Badge variant="outline" className={`${t.color} border`}>{t.label}</Badge>;
};

export const getTicketStatusBadge = (statuses, status) => {
  const statusInfo = statuses.find((s) => s.key === status);
  if (!statusInfo) return <Badge variant="secondary">{status}</Badge>;
  return (
    <Badge
      variant="secondary"
      className="gap-1"
      style={{ backgroundColor: statusInfo.color + '20', color: statusInfo.color, borderColor: statusInfo.color }}
    >
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusInfo.color }} />
      {statusInfo.name}
    </Badge>
  );
};

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  // FastAPI validation errors are a list of { loc, msg }
  if (Array.isArray(detail) && detail.length > 0) {
    return `${detail[0].loc?.slice(-1)[0]}: ${detail[0].msg}`;
  }
  return fallback;
};

export const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [appNames, setAppNames] = useState([]);
  const [projects, setProjects] = useState([]);
  const [releases, setReleases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterApp, setFilterApp] = useState('all');
  const [filterLinked, setFilterLinked] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Dialogs
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);  // null in the form dialog = creating
  const [form, setForm] = useState(EMPTY_FORM);
  const [convertForm, setConvertForm] = useState({ title: '', project_id: '', release_id: '', assigned_users: [] });
  const [storySearch, setStorySearch] = useState('');
  const [storyResults, setStoryResults] = useState([]);

  const fetchTickets = useCallback(async () => {
    try {
      const params = { page: currentPage, page_size: pageSize };
      if (searchQuery) params.search = searchQuery;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.type = filterType;
      if (filterApp !== 'all') params.app_name = filterApp;
      if (filterLinked !== 'all') params.linked = filterLinked;

      const response = await axios.get(`${API}/tickets`, { params });
      setTickets(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, filterStatus, filterType, filterApp, filterLinked]);

  const fetchAppNames = async () => {
    try {
      const response = await axios.get(`${API}/tickets/app-names`);
      setAppNames(response.data);
    } catch (error) {
      console.error('Failed to load app names');
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    const fetchStatic = async () => {
      try {
        const [statusesRes, projectsRes, usersRes] = await Promise.all([
          axios.get(`${API}/statuses`),
          axios.get(`${API}/projects`),
          axios.get(`${API}/users/list`)
        ]);
        setStatuses(statusesRes.data);
        setProjects(projectsRes.data);
        setUsers(usersRes.data);
      } catch (error) {
        console.error('Failed to load statuses, projects or users');
      }
    };
    fetchStatic();
    fetchAppNames();
  }, []);

  // Back to the first page whenever the filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterType, filterApp, filterLinked]);

  useEffect(() => {
    if (!linkDialogOpen) return;
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/user-stories/search`, { params: { q: storySearch } });
        setStoryResults(response.data);
      } catch (error) {
        setStoryResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [storySearch, linkDialogOpen]);

  const refresh = () => {
    fetchTickets();
    fetchAppNames();
  };

  const openCreateDialog = () => {
    setSelectedTicket(null);
    setForm(EMPTY_FORM);
    setFormDialogOpen(true);
  };

  const openEditDialog = (ticket) => {
    setSelectedTicket(ticket);
    setForm({
      description: ticket.description,
      customer_name: ticket.customer_name,
      app_name: ticket.app_name || '',
      type: ticket.type || '',
      email: ticket.email || '',
      phone: ticket.phone || '',
      status: ticket.status
    });
    setFormDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Empty optional fields are sent as null (an empty string is not a valid email)
    const payload = {
      description: form.description,
      customer_name: form.customer_name,
      app_name: form.app_name || null,
      type: form.type || null,
      email: form.email || null,
      phone: form.phone || null
    };
    try {
      if (selectedTicket) {
        await axios.put(`${API}/tickets/${selectedTicket.id}`, { ...payload, status: form.status });
        toast.success('Ticket updated');
      } else {
        await axios.post(`${API}/tickets`, payload);
        toast.success('Ticket created');
      }
      setFormDialogOpen(false);
      refresh();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to save ticket'));
    }
  };

  const handleDelete = async (ticket) => {
    if (!window.confirm(`Are you sure you want to delete ticket #${ticket.id}?`)) return;
    try {
      await axios.delete(`${API}/tickets/${ticket.id}`);
      toast.success('Ticket deleted');
      refresh();
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  const fetchReleasesForProject = async (projectId) => {
    try {
      const response = await axios.get(`${API}/releases`, { params: { project_id: projectId } });
      setReleases(response.data);
    } catch (error) {
      setReleases([]);
    }
  };

  const openConvertDialog = (ticket) => {
    setSelectedTicket(ticket);
    const firstLine = ticket.description.split('\n')[0];
    setConvertForm({
      title: firstLine.length > 100 ? `${firstLine.slice(0, 97)}...` : firstLine,
      project_id: '',
      release_id: '',
      assigned_users: []
    });
    setReleases([]);
    setConvertDialogOpen(true);
  };

  const handleConvert = async () => {
    try {
      const response = await axios.post(`${API}/tickets/${selectedTicket.id}/convert`, {
        title: convertForm.title,
        project_id: parseInt(convertForm.project_id),
        release_id: convertForm.release_id ? parseInt(convertForm.release_id) : null,
        assigned_users: convertForm.assigned_users
      });
      toast.success(`Ticket converted to user story ${response.data.issue_number}`);
      setConvertDialogOpen(false);
      refresh();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to convert ticket'));
    }
  };

  const toggleConvertAssignee = (userId) => {
    setConvertForm((prev) => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter((id) => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const openLinkDialog = (ticket) => {
    setSelectedTicket(ticket);
    setStorySearch('');
    setStoryResults([]);
    setLinkDialogOpen(true);
  };

  const handleLink = async (story) => {
    try {
      await axios.post(`${API}/tickets/${selectedTicket.id}/link`, { user_story_id: story.id });
      toast.success(`Ticket linked to ${story.issue_number}`);
      setLinkDialogOpen(false);
      refresh();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to link ticket'));
    }
  };

  const handleUnlink = async (ticket) => {
    try {
      await axios.delete(`${API}/tickets/${ticket.id}/link`);
      toast.success('Ticket unlinked');
      refresh();
    } catch (error) {
      toast.error('Failed to unlink ticket');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const hasFilters = searchQuery || filterStatus !== 'all' || filterType !== 'all' || filterApp !== 'all' || filterLinked !== 'all';

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Ticket className="h-8 w-8 text-primary" />
            Tickets
          </h1>
          <p className="text-muted-foreground mt-1">
            Questions, bugs and remarks from the users of our apps
          </p>
        </div>
        <Button className="gap-2" onClick={openCreateDialog} data-testid="create-ticket-btn">
          <Plus className="h-4 w-4" />
          New Ticket
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search description, customer, app, email, phone or ticket number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-tickets-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full lg:w-[150px]" data-testid="filter-ticket-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full lg:w-[140px]" data-testid="filter-ticket-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TICKET_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterApp} onValueChange={setFilterApp}>
              <SelectTrigger className="w-full lg:w-[160px]" data-testid="filter-ticket-app">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Apps</SelectItem>
                {appNames.map((name) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterLinked} onValueChange={setFilterLinked}>
              <SelectTrigger className="w-full lg:w-[160px]" data-testid="filter-ticket-linked">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Linked & unlinked</SelectItem>
                <SelectItem value="linked">Linked to a story</SelectItem>
                <SelectItem value="unlinked">Not linked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets list */}
      {tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Ticket className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-1">
              {hasFilters ? 'No tickets match these filters' : 'No tickets yet'}
            </h3>
            <p className="text-muted-foreground text-center">
              {hasFilters
                ? 'Try a different search or clear the filters'
                : 'Tickets submitted by our apps or created here will show up in this list'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-16">#</th>
                    <th>Description</th>
                    <th>Customer</th>
                    <th className="hidden md:table-cell">App</th>
                    <th>Status</th>
                    <th className="hidden lg:table-cell">User Story</th>
                    <th className="hidden md:table-cell">Created</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="cursor-pointer"
                      onClick={() => openEditDialog(ticket)}
                      data-testid={`ticket-row-${ticket.id}`}
                    >
                      <td className="font-mono text-sm text-muted-foreground">#{ticket.id}</td>
                      <td className="max-w-md">
                        <div className="flex items-center gap-2">
                          {getTicketTypeBadge(ticket.type)}
                          <span className="line-clamp-1">{ticket.description}</span>
                        </div>
                      </td>
                      <td>
                        <div className="font-medium">{ticket.customer_name}</div>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          {ticket.email && <span title={ticket.email}><Mail className="h-3 w-3" /></span>}
                          {ticket.phone && <span title={ticket.phone}><Phone className="h-3 w-3" /></span>}
                        </div>
                      </td>
                      <td className="hidden md:table-cell text-sm">{ticket.app_name || '-'}</td>
                      <td>{getTicketStatusBadge(statuses, ticket.status)}</td>
                      <td className="hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                        {ticket.user_story_id ? (
                          <Link
                            to={`/user-stories/${ticket.user_story_id}`}
                            className="font-mono text-sm hover:text-primary hover:underline"
                            title={ticket.user_story_title}
                          >
                            {ticket.user_story_issue_number}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="hidden md:table-cell text-sm text-muted-foreground">
                        {format(new Date(ticket.created_at), 'MMM d, yyyy')}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`ticket-actions-${ticket.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(ticket)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Open
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openConvertDialog(ticket)}>
                              <ArrowRightCircle className="h-4 w-4 mr-2" />
                              Convert to new story
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openLinkDialog(ticket)}>
                              <Link2 className="h-4 w-4 mr-2" />
                              Link to existing story
                            </DropdownMenuItem>
                            {ticket.user_story_id && (
                              <DropdownMenuItem onClick={() => handleUnlink(ticket)}>
                                <Unlink className="h-4 w-4 mr-2" />
                                Unlink from {ticket.user_story_issue_number}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(ticket)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Create / edit dialog */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTicket ? `Ticket #${selectedTicket.id}` : 'New Ticket'}</DialogTitle>
            <DialogDescription>
              {selectedTicket
                ? `Created ${format(new Date(selectedTicket.created_at), 'MMM d, yyyy h:mm a')} by ${selectedTicket.created_by_name}`
                : 'Register a question, bug or remark from a user'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ticket-customer">Customer name *</Label>
                <Input
                  id="ticket-customer"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  maxLength={200}
                  required
                  data-testid="ticket-customer-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ticket-app">App name</Label>
                <Input
                  id="ticket-app"
                  value={form.app_name}
                  onChange={(e) => setForm({ ...form, app_name: e.target.value })}
                  maxLength={100}
                  list="ticket-app-names"
                  data-testid="ticket-app-input"
                />
                <datalist id="ticket-app-names">
                  {appNames.map((name) => <option key={name} value={name} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ticket-email">Email</Label>
                <Input
                  id="ticket-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="ticket-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ticket-phone">Phone number</Label>
                <Input
                  id="ticket-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  maxLength={50}
                  data-testid="ticket-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={form.type || 'none'}
                  onValueChange={(value) => setForm({ ...form, type: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid="ticket-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No type</SelectItem>
                    {TICKET_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedTicket && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                    <SelectTrigger data-testid="ticket-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticket-description">Description *</Label>
              <Textarea
                id="ticket-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                maxLength={MAX_DESCRIPTION}
                rows={8}
                required
                data-testid="ticket-description-input"
              />
              <p className="text-xs text-muted-foreground text-right">
                {form.description.length}/{MAX_DESCRIPTION}
              </p>
            </div>
            {selectedTicket?.user_story_id && (
              <p className="text-sm text-muted-foreground">
                Linked to user story{' '}
                <Link
                  to={`/user-stories/${selectedTicket.user_story_id}`}
                  className="font-mono text-foreground hover:text-primary hover:underline"
                >
                  {selectedTicket.user_story_issue_number}
                </Link>
                {selectedTicket.user_story_title && ` - ${selectedTicket.user_story_title}`}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="ticket-submit-btn">
                {selectedTicket ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert to new user story */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Convert to User Story</DialogTitle>
            <DialogDescription>
              Create a new user story from ticket #{selectedTicket?.id}. The ticket is kept and linked to the story.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="convert-title">Story title *</Label>
              <Input
                id="convert-title"
                value={convertForm.title}
                onChange={(e) => setConvertForm({ ...convertForm, title: e.target.value })}
                maxLength={200}
                data-testid="convert-ticket-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select
                value={convertForm.project_id}
                onValueChange={(value) => {
                  setConvertForm({ ...convertForm, project_id: value, release_id: '' });
                  fetchReleasesForProject(parseInt(value));
                }}
              >
                <SelectTrigger data-testid="convert-ticket-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.prefix} - {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Release</Label>
              <Select
                value={convertForm.release_id || 'none'}
                onValueChange={(value) => setConvertForm({ ...convertForm, release_id: value === 'none' ? '' : value })}
                disabled={!convertForm.project_id}
              >
                <SelectTrigger data-testid="convert-ticket-release-select">
                  <SelectValue placeholder={convertForm.project_id ? 'Select release' : 'Select project first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No release (backlog)</SelectItem>
                  {releases.map((release) => (
                    <SelectItem key={release.id} value={String(release.id)}>
                      v{release.version} - {release.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign Users</Label>
              <div className="border rounded-md max-h-32 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No users available</p>
                ) : (
                  <div className="p-2 space-y-1">
                    {users.map((user) => {
                      const isChecked = convertForm.assigned_users.includes(user.id);
                      return (
                        <div
                          key={user.id}
                          className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                          onClick={() => toggleConvertAssignee(user.id)}
                        >
                          <div className={`h-4 w-4 rounded border flex items-center justify-center ${isChecked ? 'bg-primary border-primary' : 'border-input'}`}>
                            {isChecked && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                          <span className="text-sm">{user.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConvert}
              disabled={!convertForm.project_id || !convertForm.title.trim()}
              className="gap-2"
              data-testid="convert-ticket-submit-btn"
            >
              <ArrowRightCircle className="h-4 w-4" />
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to existing user story */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link to User Story</DialogTitle>
            <DialogDescription>
              Link ticket #{selectedTicket?.id} to an existing user story
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title or issue number..."
                value={storySearch}
                onChange={(e) => setStorySearch(e.target.value)}
                className="pl-9"
                autoFocus
                data-testid="link-story-search-input"
              />
            </div>
            <div className="border rounded-md max-h-72 overflow-y-auto divide-y">
              {storyResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No user stories found</p>
              ) : (
                storyResults.map((story) => (
                  <button
                    key={story.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-3 disabled:opacity-50"
                    onClick={() => handleLink(story)}
                    disabled={story.id === selectedTicket?.user_story_id}
                    data-testid={`link-story-${story.id}`}
                  >
                    <span className="font-mono text-xs text-muted-foreground shrink-0">{story.issue_number}</span>
                    <span className="text-sm truncate">{story.title}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
